import { Command } from "commander";
import { readFileSync, writeFileSync, existsSync, mkdirSync } from "node:fs";
import { resolve } from "node:path";
import chalk from "chalk";
import Table from "cli-table3";
import { loadData, getModelPricing, fetchAdminUsage, resolveAdminKey } from "@fathom/core";
import type { AdminUsageReport } from "@fathom/core";
// Inline calibration types (mirrors @fathom/router types to avoid adding dependency)
export interface CalibrationEntry {
  model: string;
  taskType: string;
  complexity: string;
  estimatedTokens: number;
  actualTokens: number;
  accuracy: number; // actual/estimated ratio
  timestamp: number;
}

export interface CalibrationHistory {
  entries: CalibrationEntry[];
}
import { confirm } from "@inquirer/prompts";
import { syncReconciliation, logConvexStatus } from "../convex-client.js";
import { getProjectName } from "../project-name.js";
import { readProjectConfig } from "../project-config.js";
import { withSpinner } from "../ux/spinner.js";
import { fatalError } from "../ux/errors.js";

interface TrackedSession {
  sessionId: string;
  featureId?: string;
  status: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  model: string;
  sessionStart?: number;
  activeMinutes?: number;
}

interface RegistryFeature {
  id: string;
  name: string;
  estimated_tokens: number;
  status: string;
}

// ── Calibration helpers (exported for testing) ─────────────────

interface SessionLike {
  sessionId: string;
  featureId?: string;
  status: string;
  model: string;
}

/**
 * Compute calibration entries from registry features and actual usage.
 * For each feature with actuals, determines dominant model and accuracy ratio.
 */
export function computeCalibrationEntries(
  features: RegistryFeature[],
  actuals: Map<string, { tokens: number; cost: number; sessions: number; minutes: number }>,
  sessions: SessionLike[],
): CalibrationEntry[] {
  const entries: CalibrationEntry[] = [];

  for (const feature of features) {
    const actual = actuals.get(feature.id);
    if (!actual) continue;

    // Find dominant model from sessions for this feature
    const featureSessions = sessions.filter(
      (s) => s.featureId === feature.id && s.status !== "untagged",
    );
    const dominantModel =
      featureSessions.length > 0 ? featureSessions[0].model : "unknown";

    entries.push({
      model: dominantModel,
      taskType: "mixed", // features span multiple task types
      complexity: "M", // default; could be refined with registry metadata
      estimatedTokens: feature.estimated_tokens,
      actualTokens: actual.tokens,
      accuracy:
        feature.estimated_tokens > 0
          ? actual.tokens / feature.estimated_tokens
          : 0,
      timestamp: Date.now(),
    });
  }

  return entries;
}

/**
 * Persist calibration entries to .fathom/store/calibration.json.
 * Merges (appends) with any existing entries.
 */
export function persistCalibration(
  entries: CalibrationEntry[],
  projectDir: string = process.cwd(),
): void {
  const calPath = resolve(projectDir, ".fathom", "store", "calibration.json");
  mkdirSync(resolve(projectDir, ".fathom", "store"), { recursive: true });

  let existing: CalibrationHistory = { entries: [] };
  try {
    existing = JSON.parse(readFileSync(calPath, "utf-8"));
  } catch {
    /* first run */
  }
  existing.entries.push(...entries);
  writeFileSync(calPath, JSON.stringify(existing, null, 2));
}

export const reconcileCommand = new Command("reconcile")
  .description("Compare estimates vs actuals per feature")
  .option("-p, --project <name>", "Project name")
  .option("--registry <path>", "Path to registry.json")
  .option("--dry-run", "Show reconciliation without syncing")
  .option("-y, --yes", "Skip confirmation prompt")
  .action(async (options: { project?: string; registry?: string; dryRun?: boolean; yes?: boolean }) => {
   try {
    const registryPath =
      options.registry ??
      resolve(process.cwd(), ".fathom", "registry.json");
    const summaryPath = resolve(
      process.cwd(),
      ".fathom",
      "tracking",
      "summary.json",
    );

    if (!existsSync(registryPath)) {
      console.log(
        chalk.yellow(
          "No registry found. Run `fathom analyze <spec> --project <name>` first.",
        ),
      );
      return;
    }

    if (!existsSync(summaryPath)) {
      console.log(
        chalk.yellow(
          "No tracking data found. Run `fathom track --project <name>` first.",
        ),
      );
      return;
    }

    let registry: { project?: string; features: RegistryFeature[] };
    try {
      registry = JSON.parse(readFileSync(registryPath, "utf-8"));
    } catch {
      fatalError("Invalid registry", "Failed to parse " + registryPath, "Delete and re-run fathom analyze");
    }
    let summary: { sessions: TrackedSession[] };
    try {
      summary = JSON.parse(readFileSync(summaryPath, "utf-8"));
    } catch {
      fatalError("Invalid tracking data", "Failed to parse " + summaryPath, "Delete and re-run fathom track --backfill");
    }
    const projectName = options.project ?? registry.project ?? getProjectName();
    const data = loadData();

    // Aggregate actuals per feature
    const actuals = new Map<
      string,
      { tokens: number; cost: number; sessions: number; minutes: number }
    >();

    for (const session of summary.sessions as TrackedSession[]) {
      if (!session.featureId || session.status === "untagged") continue;

      const existing = actuals.get(session.featureId) ?? {
        tokens: 0,
        cost: 0,
        sessions: 0,
        minutes: 0,
      };

      const totalTokens = session.inputTokens + session.outputTokens;
      const pricing = getModelPricing(data.models, session.model);
      const cost = pricing
        ? (session.inputTokens / 1_000_000) * pricing.input_per_mtok +
          (session.outputTokens / 1_000_000) * pricing.output_per_mtok +
          (session.cacheReadTokens / 1_000_000) * pricing.cache_read_per_mtok
        : 0;

      existing.tokens += totalTokens;
      existing.cost += cost;
      existing.sessions += 1;
      existing.minutes += session.activeMinutes ?? 0;

      actuals.set(session.featureId, existing);
    }

    // Display reconciliation
    console.log(
      chalk.bold(`\nFathom — Reconcile: ${projectName}`),
    );
    console.log(chalk.dim("─".repeat(75)));

    const table = new Table({
      head: [
        chalk.white("Feature"),
        chalk.white("Est. Tokens"),
        chalk.white("Act. Tokens"),
        chalk.white("Drift"),
        chalk.white("Act. Cost"),
        chalk.white("Sessions"),
      ],
      colAligns: ["left", "right", "right", "center", "right", "center"],
    });

    let totalEstimated = 0;
    let totalActual = 0;
    let totalCost = 0;

    for (const feature of registry.features as RegistryFeature[]) {
      const actual = actuals.get(feature.id);
      const estimated = feature.estimated_tokens;
      totalEstimated += estimated;

      if (actual) {
        totalActual += actual.tokens;
        totalCost += actual.cost;

        const drift = ((actual.tokens - estimated) / estimated) * 100;
        const driftLabel =
          drift > 20
            ? chalk.red(`+${drift.toFixed(0)}%`)
            : drift < -20
              ? chalk.green(`${drift.toFixed(0)}%`)
              : chalk.white(`${drift >= 0 ? "+" : ""}${drift.toFixed(0)}%`);

        table.push([
          feature.name,
          estimated.toLocaleString(),
          actual.tokens.toLocaleString(),
          driftLabel,
          `$${actual.cost.toFixed(2)}`,
          actual.sessions.toString(),
        ]);
      } else {
        table.push([
          feature.name,
          estimated.toLocaleString(),
          chalk.dim("—"),
          chalk.dim("—"),
          chalk.dim("—"),
          chalk.dim("0"),
        ]);
      }
    }

    console.log(table.toString());

    const overallDrift =
      totalEstimated > 0
        ? ((totalActual - totalEstimated) / totalEstimated) * 100
        : 0;
    const driftColor =
      Math.abs(overallDrift) > 20
        ? overallDrift > 0
          ? chalk.red
          : chalk.green
        : chalk.white;

    console.log(
      chalk.bold(
        `\nOverall: ${totalActual.toLocaleString()} / ${totalEstimated.toLocaleString()} tokens — ` +
          driftColor(
            `${overallDrift >= 0 ? "+" : ""}${overallDrift.toFixed(1)}% drift`,
          ),
      ),
    );
    console.log(chalk.dim(`Total actual cost: $${totalCost.toFixed(2)}`));
    console.log(
      chalk.dim(
        `Features with data: ${actuals.size} / ${registry.features.length}`,
      ),
    );

    // Admin API comparison (Tier 3) — only when admin key is configured
    const config = readProjectConfig();
    const adminKey = resolveAdminKey(config?.anthropicAdminKey ?? undefined);
    let adminReport: AdminUsageReport | null = null;

    if (adminKey) {
      // Find date range from tracked sessions
      const sessions = summary.sessions as TrackedSession[];
      const starts = sessions
        .map((s) => s.sessionStart)
        .filter((t): t is number => typeof t === "number" && t > 0);

      if (starts.length > 0) {
        const earliest = new Date(Math.min(...starts));
        const latest = new Date(); // up to now
        earliest.setHours(0, 0, 0, 0);

        adminReport = await withSpinner("Fetching admin usage data...", async () =>
          fetchAdminUsage({
            adminKey,
            startingAt: earliest.toISOString(),
            endingAt: latest.toISOString(),
            bucketWidth: "1d",
            groupBy: ["model"],
          })
        );

        if (adminReport) {
          const billedTotal =
            adminReport.totalInputTokens + adminReport.totalOutputTokens;
          const gap = billedTotal - totalActual;
          const gapPct = totalActual > 0 ? (gap / totalActual) * 100 : 0;

          console.log(chalk.bold("\n  Billed vs Tracked (Admin API)"));
          console.log(chalk.dim("  " + "─".repeat(45)));
          console.log(
            chalk.dim(
              `  Tracked: ${totalActual.toLocaleString()} tokens`,
            ),
          );
          console.log(
            chalk.dim(
              `  Billed:  ${billedTotal.toLocaleString()} tokens`,
            ),
          );

          const gapColor = Math.abs(gapPct) > 20 ? chalk.red : chalk.yellow;
          console.log(
            gapColor(
              `  Gap:     ${gap.toLocaleString()} tokens (${gapPct >= 0 ? "+" : ""}${gapPct.toFixed(1)}%)`,
            ),
          );

          if (adminReport.totalCacheReadTokens > 0) {
            console.log(
              chalk.dim(
                `  Cache:   ${adminReport.totalCacheReadTokens.toLocaleString()} cache-read, ${adminReport.totalCacheCreationTokens.toLocaleString()} cache-create`,
              ),
            );
          }
        } else {
          console.log(
            chalk.dim("\n  Admin API: Could not fetch usage data (check key/permissions)"),
          );
        }
      }
    }

    // ── Calibration entries ──────────────────────────────────
    const calibrationEntries = computeCalibrationEntries(
      registry.features as RegistryFeature[],
      actuals,
      summary.sessions as TrackedSession[],
    );

    if (calibrationEntries.length > 0) {
      const avgAccuracy =
        calibrationEntries.reduce((sum, e) => sum + e.accuracy, 0) /
        calibrationEntries.length;
      const pct = Math.round(
        Math.min(avgAccuracy, 1 / avgAccuracy) * 100,
      ); // symmetric accuracy %
      console.log(
        chalk.bold(
          `\n  Calibration: ${calibrationEntries.length} features reconciled. Average accuracy: ${pct}%.`,
        ),
      );
    }

    if (!options.dryRun && calibrationEntries.length > 0) {
      persistCalibration(calibrationEntries);
      console.log(
        chalk.dim(
          `  Calibration data saved to .fathom/store/calibration.json`,
        ),
      );
    }

    // Dry-run: show reconciliation only, skip sync
    if (options.dryRun) {
      console.log(chalk.dim(`\n[dry-run] Would sync reconciliation to Convex. No changes made.`));
      return;
    }

    // Confirmation prompt (skip with --yes)
    if (!options.yes) {
      const proceed = await confirm({
        message: "Sync reconciliation data? (use --yes to skip)",
        default: true,
      });
      if (!proceed) {
        console.log(chalk.dim("\n  Aborted."));
        return;
      }
    }

    // Sync to Convex
    const overallAccuracy = totalEstimated > 0
      ? 1 - Math.abs(totalActual - totalEstimated) / totalEstimated
      : 0;

    const featureDeltas = (registry.features as RegistryFeature[])
      .filter((f) => actuals.has(f.id))
      .map((f) => {
        const actual = actuals.get(f.id)!;
        const delta = actual.tokens - f.estimated_tokens;
        const accuracy = f.estimated_tokens > 0
          ? 1 - Math.abs(delta) / f.estimated_tokens
          : 0;
        return {
          featureId: f.id,
          featureName: f.name,
          estimatedTokens: f.estimated_tokens,
          actualTokens: actual.tokens,
          delta,
          accuracy,
          modelRecommended: "sonnet",
          modelUsed: "sonnet",
          sessionsCount: actual.sessions,
          activeMinutes: actual.minutes,
          tagQuality: { autoTagged: actual.sessions, likelyTagged: 0, manualTagged: 0 },
          insights: delta > 0
            ? [`Over estimate by ${((delta / f.estimated_tokens) * 100).toFixed(0)}%`]
            : [`Under estimate by ${((Math.abs(delta) / f.estimated_tokens) * 100).toFixed(0)}%`],
        };
      });

    const totalSessions = Array.from(actuals.values()).reduce((s, a) => s + a.sessions, 0);
    const autoTagged = (summary.sessions as TrackedSession[]).filter(
      (s) => s.status === "auto-tagged",
    ).length;
    const autoTagAccuracy = totalSessions > 0 ? autoTagged / totalSessions : 0;

    const synced = await withSpinner("Syncing reconciliation...", async () =>
      syncReconciliation({
        projectName,
        featureDeltas,
        overallAccuracy,
        autoTagAccuracy,
        suggestedAdjustments: [],
      })
    );
    logConvexStatus(synced);
   } catch (err: unknown) {
     if (err && typeof err === "object" && "name" in err && (err as Error).name === "ExitPromptError") {
       console.log(chalk.dim("\n  Exiting.\n"));
       return;
     }
     throw err;
   }
  });
